import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CampaignABTestService} from '../../service/api/campaign-ab-test.service';
import {CampaignABTestDto} from '../../model/dto/entity/campaign-ab-test.dto';
import {ABTestGroupDto} from '../../model/dto/entity/ab-test-group.dto';
import {
    ABTestActionResponse,
    ABTestResultsComparison,
    ApplyWinnerResponse,
    CreateABTestRequest
} from '../../model/dto/api/ab-test.interface';
import {ABTestGroupTypeEnum} from '../../enum/ab-test-group-type.enum';

/**
 * A/B Test Manager Component (F-026: Campaign A/B Testing).
 * Provides UI for configuring, running, and analyzing A/B tests.
 *
 * Acceptance Criteria:
 * - Given a campaign, when A/B test is configured, then members are randomly split into groups
 * - Given test groups exist, when processing completes, then outcomes can be compared statistically
 * - Given A/B test results, when winner is identified, then remaining members can receive winning approach
 */
@Component({
    selector: 'ct-ab-test-manager',
    templateUrl: './ab-test-manager.component.html',
    standalone: false
})
export class ABTestManagerComponent implements OnChanges {

    @Input() campaignId: number;
    @Input() campaignName: string;

    @Output() testCreated = new EventEmitter<CampaignABTestDto>();
    @Output() testStarted = new EventEmitter<ABTestActionResponse>();
    @Output() winnerSelected = new EventEmitter<ABTestActionResponse>();
    @Output() winnerApplied = new EventEmitter<ApplyWinnerResponse>();

    tests: CampaignABTestDto[] = [];
    selectedTest: CampaignABTestDto;
    testGroups: ABTestGroupDto[] = [];
    testResults: ABTestResultsComparison;

    // Create test form
    showCreateForm: boolean = false;
    newTestName: string = '';
    newTestDescription: string = '';
    newTestVariable: string = 'MESSAGING';
    newPrimaryMetric: string = 'CONVERSION_RATE';
    newMinimumSampleSize: number = 100;
    newConfidenceLevel: number = 0.95;

    // Add group form
    showAddGroupForm: boolean = false;
    newGroupName: string = '';
    newGroupType: ABTestGroupTypeEnum = ABTestGroupTypeEnum.control;
    newAllocationPercentage: number = 50;
    newConfigurationJson: string = '';

    loading: boolean = false;
    error: string = '';

    testVariables = [
        {value: 'MESSAGING', label: 'Nachricht'},
        {value: 'OFFER', label: 'Angebot'},
        {value: 'CONTACT_METHOD', label: 'Kontaktmethode'},
        {value: 'CONTACT_TIMING', label: 'Kontaktzeitpunkt'},
        {value: 'CONTACT_FREQUENCY', label: 'Kontakthäufigkeit'},
        {value: 'AGENT_TYPE', label: 'Agententyp'},
        {value: 'PROCESS_FLOW', label: 'Prozessablauf'},
        {value: 'OTHER', label: 'Sonstiges'}
    ];

    primaryMetrics = [
        {value: 'CONVERSION_RATE', label: 'Konversionsrate'},
        {value: 'RESPONSE_RATE', label: 'Antwortrate'},
        {value: 'TIME_TO_CONVERSION', label: 'Zeit bis Konversion'},
        {value: 'TIME_TO_RESPONSE', label: 'Zeit bis Antwort'},
        {value: 'CONTACTS_PER_CONVERSION', label: 'Kontakte pro Konversion'},
        {value: 'REVENUE_PER_MEMBER', label: 'Umsatz pro Mitglied'},
        {value: 'ENGAGEMENT_SCORE', label: 'Engagement-Score'}
    ];

    groupTypes = [
        {value: ABTestGroupTypeEnum.control, label: 'Kontrollgruppe'},
        {value: ABTestGroupTypeEnum.variant, label: 'Variante'}
    ];

    constructor(private abTestService: CampaignABTestService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['campaignId'] && this.campaignId) {
            this.loadTests();
        }
    }

    loadTests(): void {
        if (!this.campaignId) return;

        this.loading = true;
        this.error = '';

        this.abTestService.getTestsForCampaign(this.campaignId).subscribe({
            next: (tests) => {
                this.tests = tests;
                if (tests.length > 0 && !this.selectedTest) {
                    this.selectTest(tests[0]);
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Tests konnten nicht geladen werden';
                this.loading = false;
            }
        });
    }

    selectTest(test: CampaignABTestDto): void {
        this.selectedTest = test;
        this.loadTestGroups();
        this.loadTestResults();
    }

    loadTestGroups(): void {
        if (!this.selectedTest) return;

        this.abTestService.getTestGroups(this.selectedTest.id).subscribe({
            next: (groups) => {
                this.testGroups = groups;
            },
            error: (err) => {
                console.error('Failed to load test groups', err);
            }
        });
    }

    loadTestResults(): void {
        if (!this.selectedTest) return;

        this.abTestService.getResults(this.selectedTest.id).subscribe({
            next: (results) => {
                this.testResults = results;
            },
            error: (err) => {
                console.error('Failed to load test results', err);
            }
        });
    }

    createTest(): void {
        if (!this.newTestName.trim()) {
            this.error = 'Testname ist erforderlich';
            return;
        }

        const request: CreateABTestRequest = {
            campaignId: this.campaignId,
            name: this.newTestName,
            description: this.newTestDescription,
            testVariable: this.newTestVariable,
            primaryMetric: this.newPrimaryMetric,
            minimumSampleSize: this.newMinimumSampleSize,
            confidenceLevel: this.newConfidenceLevel
        };

        this.loading = true;
        this.abTestService.createABTest(request).subscribe({
            next: (response) => {
                if (response.success) {
                    this.showCreateForm = false;
                    this.resetCreateForm();
                    this.loadTests();
                    this.testCreated.emit(null);
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht erstellt werden';
                this.loading = false;
            }
        });
    }

    addGroup(): void {
        if (!this.selectedTest || !this.newGroupName.trim()) {
            this.error = 'Gruppenname ist erforderlich';
            return;
        }

        this.loading = true;
        this.abTestService.addTestGroup(this.selectedTest.id, {
            name: this.newGroupName,
            groupType: this.newGroupType,
            allocationPercentage: this.newAllocationPercentage / 100,
            configurationJson: this.newConfigurationJson || null
        }).subscribe({
            next: (response) => {
                if (response.success) {
                    this.showAddGroupForm = false;
                    this.resetAddGroupForm();
                    this.loadTestGroups();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Testgruppe konnte nicht hinzugefügt werden';
                this.loading = false;
            }
        });
    }

    startTest(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.startABTest(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                    this.loadTestResults();
                    this.testStarted.emit(response);
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht gestartet werden';
                this.loading = false;
            }
        });
    }

    pauseTest(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.pauseABTest(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht pausiert werden';
                this.loading = false;
            }
        });
    }

    resumeTest(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.resumeABTest(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht fortgesetzt werden';
                this.loading = false;
            }
        });
    }

    endTestForDecision(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.endTestForDecision(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                    this.loadTestResults();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht beendet werden';
                this.loading = false;
            }
        });
    }

    refreshResults(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.refreshResults(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTestResults();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Ergebnisse konnten nicht aktualisiert werden';
                this.loading = false;
            }
        });
    }

    selectWinner(groupId: number): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.selectWinner(this.selectedTest.id, groupId).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                    this.winnerSelected.emit(response);
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Gewinner konnte nicht ausgewählt werden';
                this.loading = false;
            }
        });
    }

    applyWinnerToRemainingMembers(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.applyWinnerToRemainingMembers(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                    this.winnerApplied.emit(response);
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Gewinner konnte nicht angewendet werden';
                this.loading = false;
            }
        });
    }

    cancelTest(): void {
        if (!this.selectedTest) return;

        this.loading = true;
        this.abTestService.cancelABTest(this.selectedTest.id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.loadTests();
                } else {
                    this.error = response.message;
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'A/B-Test konnte nicht abgebrochen werden';
                this.loading = false;
            }
        });
    }

    canStartTest(): boolean {
        return this.selectedTest &&
               (this.selectedTest.status as unknown as string) === 'DRAFT' &&
               this.testGroups.length >= 2;
    }

    canPauseTest(): boolean {
        return this.selectedTest &&
               (this.selectedTest.status as unknown as string) === 'RUNNING';
    }

    canResumeTest(): boolean {
        return this.selectedTest &&
               (this.selectedTest.status as unknown as string) === 'PAUSED';
    }

    canEndTest(): boolean {
        return this.selectedTest &&
               ((this.selectedTest.status as unknown as string) === 'RUNNING' ||
                (this.selectedTest.status as unknown as string) === 'PAUSED');
    }

    canSelectWinner(): boolean {
        return this.selectedTest &&
               (this.selectedTest.status as unknown as string) === 'AWAITING_DECISION';
    }

    canApplyWinner(): boolean {
        return this.selectedTest &&
               (this.selectedTest.status as unknown as string) === 'COMPLETED' &&
               this.selectedTest.winningGroupDtoId != null;
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'DRAFT': return 'text-gray-600 bg-gray-100';
            case 'SCHEDULED': return 'text-blue-600 bg-blue-100';
            case 'RUNNING': return 'text-green-600 bg-green-100';
            case 'PAUSED': return 'text-yellow-600 bg-yellow-100';
            case 'AWAITING_DECISION': return 'text-orange-600 bg-orange-100';
            case 'COMPLETED': return 'text-teal-600 bg-teal-100';
            case 'CANCELLED': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    }

    getGroupTypeClass(groupType: string): string {
        switch (groupType?.toUpperCase()) {
            case 'CONTROL': return 'text-blue-600 bg-blue-100';
            case 'VARIANT': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    }

    private resetCreateForm(): void {
        this.newTestName = '';
        this.newTestDescription = '';
        this.newTestVariable = 'MESSAGING';
        this.newPrimaryMetric = 'CONVERSION_RATE';
        this.newMinimumSampleSize = 100;
        this.newConfidenceLevel = 0.95;
    }

    private resetAddGroupForm(): void {
        this.newGroupName = '';
        this.newGroupType = ABTestGroupTypeEnum.control;
        this.newAllocationPercentage = 50;
        this.newConfigurationJson = '';
    }

    clearError(): void {
        this.error = '';
    }
}
